import { echo } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ClientEvents/ClientEventsService';
import { Dialog } from 'components/Dialogs/Dialog';
import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import './ServiceDisconnectedPopup.less';
import { getIsMobile } from 'config/PortalConfiguration';
import { renderComponent } from 'utils/RenderingUtils';
import { Button } from 'components/buttons/Button/Button';

const getClassName = (isMobile?: boolean) =>
  classNames('dp-service-disconnected', {
    'dp-service-disconnected--mobile': isMobile,
  });

const getButtonClassName = () => classNames('dp-service-disconnected__button');

export function ServiceDisconnectedPopup() {
  const [timer, setTimer] = useState(0);
  const [reconnectInSeconds, setReconnectInSeconds] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const isMobile = getIsMobile();
  const checkConnection = useCallback(async () => {
    try {
      setReconnecting(true);
      await echo();
      window.location.reload();
    } catch (err) {
      if (reconnectInSeconds < 10) {
        setReconnectInSeconds(val => val + 2);
      }
      setTimer(reconnectInSeconds);
      setReconnecting(false);
    }
  }, [reconnectInSeconds]);

  useEffect(() => {
    if (timer === 0) {
      checkConnection();
      return;
    }

    const timeout = setTimeout(() => setTimer(val => val - 1), 1000);

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [checkConnection, timer]);

  return (
    <Dialog
      contentClassName="dp-service-disconnected__dialog"
      holderClassName="dp-service-disconnected-dialog-holder"
      open={true}
      styles={{
        height: isMobile ? 210 : 110,
        width: isMobile ? 316 : 270,
      }}
      dialogPosition={{
        top: !isMobile ? -32 : undefined,
      }}
      isResponsive={true}
      hideTitle={true}
    >
      <div className={getClassName(isMobile)}>
        <svg
          width="60px"
          height="60px"
          viewBox="0 0 60 60"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          className="dp-service-disconnected__image"
        >
          <title>No connection</title>
          <g id="No-connection" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g id="no-connectionAsset-1" transform="translate(0.000000, 2.000000)" fill="#ED1C24" fill-rule="nonzero">
              <path
                d="M52.7701657,19.8367822 C52.8360401,18.2736132 52.5830691,16.7133114 52.0262634,15.2484626 C50.9091874,12.2349343 48.6020741,9.796937 45.6318572,8.49129785 C42.6616403,7.1856587 39.2820012,7.12389016 36.2648336,8.32009987 C32.1265683,2.17705509 24.7063406,-0.979283821 17.3394261,0.269807811 C9.97251162,1.51889944 4.04530991,6.93835168 2.22200489,14.092206 C1.39649647,17.2592422 1.44787906,20.5869337 2.37078535,23.7276773 C0.0891561019,26.7580112 -0.593689362,30.6795359 0.531995754,34.2876932 C1.65768087,37.8958505 4.45731146,40.7592549 8.07093667,41.9983662 C9.22589011,42.3872997 10.4316432,42.6098294 11.6509665,42.6590843 L12.0322164,42.6590843 C12.4828888,42.657047 12.9330708,42.6294717 13.3805393,42.5764945 L13.3805393,42.5764945 L13.5386185,42.5764945 C14.6408827,50.8303215 21.7706735,57 30.2066793,57 C38.642685,57 45.7724758,50.8303215 46.87474,42.5764945 C46.9645571,42.581484 47.0545875,42.581484 47.1444046,42.5764945 L47.6372399,42.5764945 C48.8789271,42.6264619 50.1203501,42.4713838 51.3102574,42.1176626 L51.4311415,42.1176626 C56.2725496,40.6844484 59.6887016,36.4160467 59.9799053,31.4361829 C60.2711091,26.456319 57.375178,21.8284431 52.7329706,19.8551355 L52.7701657,19.8367822 Z M30.239225,52.5423248 C26.965833,52.5423248 23.8267817,51.2578038 21.5138889,48.9718482 C19.2009962,46.6858926 17.9041072,43.5861401 17.9090304,40.3557478 L17.9090304,40.273158 C17.9090304,33.5528337 23.4294544,28.1049345 30.239225,28.1049345 C37.0489956,28.1049345 42.5694195,33.5528337 42.5694195,40.273158 L42.5694195,40.3557478 C42.5743427,43.5861401 41.2774538,46.6858926 38.9645611,48.9718482 C36.6516683,51.2578038 33.512617,52.5423248 30.239225,52.5423248 L30.239225,52.5423248 Z M48.8367823,37.9606449 L47.0328192,37.9606449 L46.8468437,37.9606449 C45.6541958,29.7964115 38.5651029,23.7366717 30.2066793,23.7366717 C21.8482556,23.7366717 14.7591627,29.7964115 13.5665149,37.9606449 L13.3805393,37.9606449 L12.9156003,37.9606449 C12.2986258,38.0449634 11.6728192,38.0449634 11.0558446,37.9606449 L10.7303874,37.9055851 C8.11070207,37.4428798 5.94776247,35.6213927 5.0727189,33.1410643 C4.19767532,30.660736 4.74667019,27.9074649 6.50874185,25.9392474 C7.17940013,25.2074106 7.3899838,24.1718446 7.0573698,23.2413154 C6.13835553,20.6580215 6.01539795,17.8639501 6.70401621,15.211756 C8.13298701,9.56591857 12.9389451,5.37246098 18.7918822,4.66444109 C24.6448193,3.95642119 30.3348086,6.88020179 33.1032488,12.0182855 C33.4539908,12.6796209 34.0666967,13.1679985 34.7956265,13.3672515 C35.5269768,13.5672085 36.3093113,13.4644882 36.962242,13.0827757 C38.2696498,12.3137058 39.7879608,11.9671882 41.3047716,12.0916986 C44.477111,12.3611668 47.1239197,14.5932091 47.8883069,17.6435654 C48.1313879,18.6168605 48.1662243,19.6294727 47.9905934,20.6167966 C47.7356032,22.0929668 48.7229864,23.4995475 50.2130015,23.7827372 C51.6151075,24.0160195 52.8713617,24.7756561 53.7186411,25.9025408 C57.586933,31.3167581 54.1556837,37.3458101 48.8739774,37.9606449 L48.8367823,37.9606449 Z"
                id="Shape"
              ></path>
              <path
                d="M36.261001,33.5976385 C35.3742382,32.8884846 34.0523709,32.9897737 33.2965141,33.8247946 L30.0400032,37.4592931 L26.7834922,33.8247946 C26.3106598,33.2327986 25.5317976,32.9385467 24.7605422,33.0605299 C23.9892868,33.1825132 23.3525778,33.7006547 23.1068086,34.4063062 C22.8610394,35.1119577 23.0466608,35.8889774 23.5889258,36.4244707 L27.2967467,40.5469342 L23.6066242,44.6693978 C23.2426789,45.0715948 23.0623403,45.5951081 23.1055218,46.1240711 C23.1487033,46.6530341 23.4118438,47.143824 23.8367038,47.4878168 C24.2608676,47.832836 24.8122055,48.0028529 25.368623,47.960217 C25.9250405,47.9175811 26.4406217,47.6658107 26.8011906,47.2606606 L30.0400032,43.5925094 L33.2965141,47.2270079 C33.6957565,47.6742645 34.2812104,47.9325794 34.8982219,47.9337159 C35.398357,47.9348661 35.8821756,47.7646139 36.261001,47.454164 C37.1439506,46.7378047 37.2508089,45.4772978 36.4999297,44.6357451 L32.8098073,40.5132815 L36.4999297,36.3908179 C37.2282225,35.5516288 37.1223488,34.3139194 36.261001,33.5976385 Z"
                id="Path"
              ></path>
            </g>
          </g>
        </svg>
        <div className="dp-service-disconnected__no-connection-text">No Connection</div>
        <div className="dp-service-disconnected__reconnecting">
          Reconnecting{!reconnecting && <span> in {timer} seconds</span>}...
        </div>
        <Button className={getButtonClassName()} variant="secondary" onClick={checkConnection}>
          Reconnect
        </Button>
      </div>
    </Dialog>
  );
}

export function renderServiceDisconnectedPopup() {
  const id = 'service-disconnected-popup-container';
  // if service disconnected container is not present then add it in the DOM
  if (!document.getElementById(id)) {
    const container = document.createElement('div');
    container.id = id;
    Decisions.IsSHMAvailable = false;
    document.body.appendChild(container);

    renderComponent({
      container,
      component: <ServiceDisconnectedPopup />,
      autoUnmount: true,
    });
  }
}
